<template>
  <div class="import-list">

    <div class="clearfix" style="padding: 1rem;">
      <div class="import-source-selector">
        <label for="source">Import from:</label>
        <select id="source" v-model="source" @change="sourceChanged">
          <option value="">Select source</option>
          <option value="buildout">BuildOut</option>
          <option value="rets">RETS</option>
        </select>
      </div>
      <div class="import-reload">
        <div class="pointer" @click="forceReloadBuildOutData" v-if="source === 'buildout'"><v-icon>sync</v-icon> Reload data</div>
        <div class="pointer" @click="forceReloadRetsData" v-if="source === 'rets'"><v-icon>sync</v-icon> Reload data</div>
      </div>
    </div>


    <div>

      <div v-if="loading" class="property-container" style="text-align: center; padding: 5rem; min-height: 50vh;">
        <InlineSpinner />
      </div>

      <div v-else>

        <div class="property-container property-container-noimport" v-if="source === ''">
          <div style="min-height: 53vh">
          </div>
        </div>

        <div class="property-container" v-if="source === 'buildout' || source === 'rets'" style="overflow: scroll; height: 50vh;">

          <div style="padding: 0 0 1rem;" v-if="properties.length > 0">
            <input type="text" placeholder="Filter results" v-model="filterText" />
          </div>

          <div>
            Select a property:
          </div>

          <div v-for="property in filteredProperties" :key="property.id"
               class="property" :class="{ selected: selectedPropertyId === property.id }"
               @click="chooseProperty(property)">

            <div class="property-left" :style="'backgroundImage: url(' + photoUrl(property) + ')'">
            </div>

            <div class="property-right">
              <div class="address">
                {{ property.address }}
              </div>
              <div class="city">
                {{ property.zip }}
                {{ property.city }},
                {{ property.county }},
                {{ property.state }}
              </div>
              <div style="font-size:0.8em">
                <span v-if="property.type">{{ propertyTypeLabel(property.type) }}, </span>
                <span v-if="property.yearBuilt">{{ property.yearBuilt }}, </span>
                <span v-if="property.buildingSize">{{ property.buildingSize | prettySF }}, </span>
                <span v-if="property.lotSize">lot {{ property.lotSize | prettySF }}, </span>
                <span v-if="property.apn">APN: {{ property.apn }}</span>
              </div>
            </div>

          </div>

          <div v-if="properties.length === 0" class="property-no-results">
            No property data found from this source.
          </div>

          <div v-else-if="filteredProperties.length === 0" class="property-no-results">
            No results with this filtering criteria.
          </div>

        </div>


        <div class="button-container">
          <button class="btn-neutral" @click="closeDialog">Cancel</button>
          <button class="wider" @click="confirmProperty()" :disabled="source !== '' && !selectedProperty.id">Confirm</button>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
  import { propertyTypeLabel } from '../utils/PfxUtils.js'
  import InlineSpinner from './elements/InlineSpinner.vue'

  export default {
    name: 'CreateListingImportDialog',
    components: {
      InlineSpinner
    },
    props: {
      selectedId: {
        type: String,
        required: false,
        default: () => ''
      }
    },
    data() {
      return {
        loading: false,
        source: '',
        properties: [],
        selectedProperty: {},
        filterText: ''
      }
    },
    computed: {
      editMode() {
        return this.$store.state.newAuctionStorage.edit === true
      },
      selectedPropertyId() {
        return this.selectedProperty.id || this.selectedId
      },
      filteredProperties() {
        const needle = this.filterText.toLowerCase()
        if (this.filterText.length > 0 && this.properties.length > 0) {
          return this.properties.filter(p => {
            const haystack = p.name + ' ' + p.address + ' ' + p.zip + ' ' + p.city + ' ' + p.county + ' ' + p.apn
            return haystack.toLowerCase().indexOf(needle) > -1
          })
        }
        return this.properties
      }
    },
    created() {
      this.source = this.$store.state.newAuctionStorage.auctionMetadata.source || ''
      this.getData(false)
    },
    methods: {
      propertyTypeLabel,

      closeDialog() {
        this.selectedProperty = {}
        this.$emit('closeImportDialog')
      },

      sourceChanged() {
        if (this.source === '') {
          this.selectedProperty = {}
        }
        this.getData(false)
      },

      chooseProperty(property) {
        this.selectedProperty = property
      },

      confirmProperty() {
        if (this.editMode) {
          this.$store.commit('openDialog', {
            title: 'Warning!',
            text: 'You are editing an existing auction. If you wish to re-import data from BuildOut/RETS, ' +
            'note that in case you have earlier edited the property data during the later steps, those ' +
            'edits will be now overwritten. Are you sure you wish to re-import?',
            buttons: [
              { title: 'Cancel', class: 'btn-neutral' },
              {
                title: 'Yes, re-import data',
                handler: () => {
                  this.$emit('importSelected', this.source, this.selectedProperty)
                  this.$store.commit('closeDialog')
                }
              }
            ]
          })
        } else {
          this.$emit('importSelected', this.source, this.selectedProperty)
        }
      },

      photoUrl(property) {
        if (property.photos.length > 0) {
          return property.photos[0]
        }
        return require('../assets/placeholder-photos.jpg')
      },

      getData(refresh) {
        if (this.source === '') {
          this.properties = []
          return
        }
        // use cached data if available
        if (this.source === 'buildout' && this.$store.state.importData.buildOut && this.$store.state.importData.buildOut.cached) {
          this.properties = this.$store.state.importData.buildOut.data
        }
        else if (this.source === 'rets' && this.$store.state.importData.rets && this.$store.state.importData.rets.cached) {
          this.properties = this.$store.state.importData.rets.data
        }
        // or load data from backend
        else {
          const that = this
          that.loading = true
          that.properties = []

          this.$PfxBackendApi.getTenantData().then(res => {
            console.log('getTenantData res', res)
            const tenantData = res.data || {}
            const buildOutApiKey = tenantData.buildOutApiKey || ''
            const retsPrincipal = tenantData.retsPrincipal || ''
            const retsPassword = tenantData.retsPassword || ''
            const retsMlsId = tenantData.retsMlsId || ''

            if (this.source === 'buildout') {
              this.$PfxBackendApi.getImportBuildOut(buildOutApiKey, refresh).then(res => {
                console.log('getImportBuildOut res', res)
                const data = res && res.data && Array.isArray(res.data) ? res.data : []
                that.properties = data
                that.$store.commit('setBuildOutData', { cached: true, data: data })
              }).catch(err => {
                console.error('getImportBuildOut err', err)
                that.$store.commit('setBuildOutData', { cached: true, data: [] })
                const message = err.data.message === 'invalid_credentials' ?
                  'Your BuildOut API key is not set correctly. Please edit the Data Import Settings in Company Profile or contact your company administrator.' :
                  'BuildOut service is not currently responding. Try again later.'
                that.$store.commit('openDialog', message)
              }).then(() => {
                that.loading = false
              })
            }
            else if (this.source === 'rets') {
              this.$PfxBackendApi.getImportRets(retsPrincipal, retsPassword, retsMlsId, refresh).then(res => {
                console.log('getImportRets res', res)
                const data = res && res.data && Array.isArray(res.data) ? res.data : []
                that.properties = data
                that.$store.commit('setRetsData', { cached: true, data: data })
              }).catch(err => {
                console.error('getImportRets err', err)
                that.$store.commit('setRetsData', { cached: true, data: [] })
                const message = err.data.message === 'invalid_credentials' ?
                  'Your REST credentials are not set correctly. Please edit the Data Import Settings in Company Profile or contact your company administrator.' :
                  'RETS service is not currently responding. Try again later.'
                that.$store.commit('openDialog', message)
              }).then(() => {
                that.loading = false
              })
            }
            else {
              this.$notify({ type: 'danger', message: 'Invalid source: [' + this.source + ']' })
              that.loading = false
            }

          }).catch(err => {
            console.error('getTenantData  err', err)
            this.$notify({ type: 'danger', message: 'Cannot fetch tenant profile data' })
          })

        }
      },

      forceReloadBuildOutData() {
        this.$store.commit('setBuildOutData', [])
        this.getData(true)
      },
      forceReloadRetsData() {
        this.$store.commit('setRetsData', [])
        this.getData(true)
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .import-list {
    background-color: $color-pure-white;
  }

  .import-source-selector {
    @include column(1/2);
    select {
      padding: 0 1em;
      display: inline-block;
      border: 0;
      font-weight: 600;
    }
  }
  .import-reload {
    @include column(1/2);
    text-align: right;
  }

  .property-container-noimport {
    text-align: center;
  }

  .property-container {
    @include clearfix();
    margin: 0 1rem 1rem;
  }
  .property-left {
    width: 100px;
    height: 75px;
    background-position: center;
    background-size: cover;
    float: left;
    margin: .25rem;
  }
  .property-right {
    margin-left: 100px;
    padding-left: 1em;

    .address {
      padding-top: 0.5em;
      font-weight: 600;
      line-height: 1em;
    }
    .city {
      font-size: 0.8em;
      font-weight: 600;
      line-height: 1em;
    }
  }

  .property-no-results {
    padding: 1rem;
    text-align: center;
  }

  .property {
    cursor: pointer;
    @include column(1/1);
  }
  .property.selected {
    background-color: #0065bf;
    color: $color-white;
  }

  .button-container {
    text-align: center;
    padding: 1em;
  }

</style>



// WEBPACK FOOTER //
// src/components/CreateListingImportDialog.vue